import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import useReceivedEmailsCount from '../hooks/useReceivedEmailsCount';
import useEmailFollowUpEnabled from '../hooks/useEmailFollowUpEnabled';

interface NavItem {
  to: string;
  icon: string;
  label: string;
  badge?: number;
}

const MobileNavigation: React.FC = () => {
  const location = useLocation();
  const { viewingAllAccounts } = useAuth();
  const { unreadCount } = useReceivedEmailsCount();
  const { emailFollowUpEnabled } = useEmailFollowUpEnabled();

  // Define the main navigation items for mobile
  const navItems: NavItem[] = [
    {
      to: '/signatures/list',
      icon: 'ki-home',
      label: 'Home'
    },
    {
      to: '/services/list',
      icon: 'ki-questionnaire-tablet',
      label: 'Services'
    },
    {
      to: '/follow-up-messages/review',
      icon: 'ki-sms',
      label: 'Marketing'
    }
  ];

  // All nav items are always shown now
  const filteredNavItems = navItems;

  const isActive = (path: string) => {
    // Check if current path starts with the nav item path
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-coal-600 border-t border-gray-200 dark:border-coal-100 z-50">
      <div className="flex items-center justify-around h-16">
        {filteredNavItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`flex flex-col items-center justify-center flex-1 h-full px-2 relative transition-colors ${
              isActive(item.to)
                ? 'text-primary'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {/* Active indicator */}
            {isActive(item.to) && (
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary" />
            )}
            
            <div className="relative">
              <i className={`ki-filled ${item.icon} text-xl`}></i>
              {item.badge && item.badge > 0 && (
                <span className="absolute -top-1 -right-2 bg-warning text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </div>
            <span className="text-xs mt-1">{item.label}</span>
          </Link>
        ))}
        
        {/* More menu button */}
        <button
          className="flex flex-col items-center justify-center flex-1 h-full px-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          onClick={() => {
            // Toggle mobile menu drawer
            const drawer = document.getElementById('mobile-menu-drawer');
            if (drawer) {
              drawer.classList.toggle('show');
            }
          }}
        >
          <div className="relative">
            <i className="ki-filled ki-menu text-xl"></i>
            {unreadCount > 0 && (viewingAllAccounts || emailFollowUpEnabled) && (
              <span className="absolute -top-1 -right-2 bg-warning text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          <span className="text-xs mt-1">More</span>
        </button>
      </div>
    </nav>
  );
};

export default MobileNavigation;